import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {isOk} from '../../utils/apiResponse';
import {LocalStorage} from '../../utils/LocalStorage';

/**
 * El código de prueba (CY-T, el Cyber 2026): `agency/subscription/trial-code/` del backend
 * (`subscription/trial_codes.py`). `GET` dice si a la agencia le sirve cargar uno hoy; `POST` lo aplica.
 * Sólo el admin de la agencia: al resto el backend le contesta el 404 de siempre, que acá es «no disponible».
 */
export const TRIAL_CODE_URL = API_URL + 'agency/subscription/trial-code/';
export const ORIGIN_APP = 'app';
export const ORIGIN_SIGNUP = 'signup';

// Los rechazos tipados del backend. Cualquier otra respuesta (un 404 del gate, un 500) va al genérico: nunca
// se muestra el `error_message` crudo, que puede ser el `detail` en inglés de DRF.
const ERROR_KEYS = {
    INVALID_CODE: 'trial_code.error_invalid',
    ALREADY_USED: 'trial_code.error_already_used',
    NOT_IN_TRIAL: 'trial_code.error_not_in_trial',
    NETWORK_ERROR: 'trial_code.error_network',
};

export function trialCodeErrorKey(response) {
    return ERROR_KEYS[response && response.status] || 'trial_code.error_generic';
}

// Un «no» definitivo del backend sobre ESE código (no la red, ni un 404 de un backend sin deployar, ni un 500):
// sólo entonces deja de tener sentido volver a ofrecerlo prellenado.
const DEFINITIVE_REJECTIONS = ['INVALID_CODE', 'ALREADY_USED', 'NOT_IN_TRIAL'];

export function isDefinitiveRejection(response) {
    return DEFINITIVE_REJECTIONS.includes(response && response.status);
}

export function fetchTrialCodeAvailability() {
    return new APIClient(TRIAL_CODE_URL).call('GET')
        .then((response) => isOk(response) && !!response.data && response.data.available === true);
}

export function redeemTrialCode(code, origin = ORIGIN_APP) {
    return new APIClient(TRIAL_CODE_URL).call('POST', {code, origin});
}

// Lo que se avisa a las demás pantallas montadas cuando se aplica un código (el banner y la tarjeta de
// Suscripción conviven): el acceso desaparece en todas, no sólo en la que se usó (review C-cy-t-03).
export const TRIAL_CODE_REDEEMED_EVENT = 'dashbo:trial-code-redeemed';

// Cuánto vale la marca del canje frente al chequeo periódico. El backend cachea la suscripción 60 s por
// worker (LocMem, `--workers 2`) y el `APIClient` 30 s: pasado esto, la respuesta ya es la nueva.
export const RECENT_REDEMPTION_MS = 90 * 1000;

/**
 * Pone al día lo que la app tiene guardado de la prueba sin esperar al próximo chequeo: el estado (una
 * prueba vencida vuelve a estar en curso), los días y el próximo paso del banner, que se vuelve a pedir
 * porque con 30 días ya no es «elige tu plan». Si la respuesta trae otros días (la caché del backend
 * todavía no se enteró), se queda con los del canje. Deja además una marca corta que el chequeo periódico
 * respeta (`isOlderThanRecentRedemption`): sin ella, una respuesta vieja devolvía a una prueba vencida a
 * «tu prueba terminó» (review C-cy-t-02).
 */
export function applyRedeemedTrial(data) {
    LocalStorage.removeSignupTrialCode();
    if (!data || !Number.isInteger(data.days_remaining)) return;
    LocalStorage.saveRecentTrialRedemption(data.days_remaining);
    APIClient.clearCache('get-logged-user-data/');
    LocalStorage.updateAgencyStatus('Free Trial');
    LocalStorage.updateFreeTrialDaysRemaining(data.days_remaining);
    const step = LocalStorage.fetchTrialNextStep();
    if (step) LocalStorage.updateTrialNextStep({...step, days_remaining: data.days_remaining});
    window.dispatchEvent(new Event(TRIAL_CODE_REDEEMED_EVENT));

    const sessionToken = LocalStorage.fetchUserToken();
    new APIClient(API_URL + 'get-logged-user-data/').call('GET').then((user) => {
        if (!isOk(user) || LocalStorage.fetchUserToken() !== sessionToken) return;
        const nextStep = user.trial_next_step;
        if (nextStep && nextStep.days_remaining === data.days_remaining) {
            LocalStorage.updateTrialNextStep(nextStep);
        }
    });
}

/**
 * Si una respuesta de `get-logged-user-data/` es más VIEJA que un canje de los últimos 90 s: dice que la
 * prueba no está en curso, o le da menos días que el canje. El chequeo periódico no la escribe ni redirige.
 */
export function isOlderThanRecentRedemption(userData) {
    const recent = LocalStorage.fetchRecentTrialRedemption();
    if (!recent || Date.now() - recent.at > RECENT_REDEMPTION_MS || !userData) return false;
    if (userData.agency_status !== 'Free Trial') return true;
    const days = userData.free_trial_days_remaining;
    return Number.isInteger(days) && days < recent.days_remaining;
}

/** `2026-11-23` → `23/11`. */
export function dayAndMonth(isoDate) {
    const [, month, day] = String(isoDate || '').split('-');
    return day && month ? `${day}/${month}` : '';
}
